import { sortHand } from '@cardengine/shared';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useState } from 'react';
import { useStore } from '../store.js';
import { ActionBar } from './ActionBar.js';
import { FlyingCards } from './FlyingCards.js';
import { LastAction } from './GameLog.js';
import { HAND_DROP_ID, Hand } from './Hand.js';
import { DISCARD_DROP_ID, DRAW_DRAG_ID, DiscardPile, DrawPile } from './Piles.js';
import { PlayerSeat } from './PlayerSeat.js';
import { CardBack, PlayingCard } from './PlayingCard.js';
import { SuitPicker } from './SuitPicker.js';

export function Table() {
  const game = useStore((s) => s.game);
  const selectedCardIds = useStore((s) => s.selectedCardIds);
  const playCards = useStore((s) => s.playCards);
  const drawCard = useStore((s) => s.drawCard);
  const highlightPlayable = useStore((s) => s.highlightPlayable);
  const setHighlightPlayable = useStore((s) => s.setHighlightPlayable);
  const rejection = useStore((s) => s.rejection);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const sensors = useSensors(
    // A small activation distance keeps taps working as clicks on touch devices.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  if (!game) return null;

  const opponents = game.players.filter((p) => p.id !== game.yourPlayerId);
  const draggingCard = draggingId ? game.yourHand.find((c) => c.id === draggingId) : undefined;

  const drawMove = game.yourLegalMoves.find((m) => m.kind === 'draw');
  const drawCount = drawMove?.kind === 'draw' ? drawMove.count : 1;

  // The hint follows what the player is actually doing, so the multi-card play
  // is discoverable at the moment it becomes possible.
  const selectedRank =
    selectedCardIds.length > 0
      ? game.yourHand.find((c) => c.id === selectedCardIds[0])?.rank
      : undefined;
  const moreOfRank =
    selectedRank !== undefined &&
    game.rules.playMultipleSameRank &&
    game.yourHand.some((c) => c.rank === selectedRank && !selectedCardIds.includes(c.id));

  const bluffAvailable =
    game.rules.bluffing &&
    game.currentPlayerId === game.yourPlayerId &&
    game.yourLegalMoves.some((m) => m.kind === 'play') &&
    game.yourLegalMoves.some((m) => m.kind === 'draw');

  const handHint = moreOfRank
    ? `Tap another ${selectedRank} to lay them together, or drag to the pile to play.`
    : selectedCardIds.length > 0
      ? 'Drag to the pile or press Play.'
      : bluffAvailable
        ? 'Play a card — or bluff by dragging the deck into your hand instead, which ends your turn.'
        : game.rules.playMultipleSameRank
          ? 'Drag a card onto the pile, or tap to select — then tap more of the same rank to lay several at once.'
          : 'Drag a card onto the pile, or tap to select and press Play.';

  function handleDragStart(event: DragStartEvent) {
    const id = String(event.active.id);
    setDraggingId(id);
    if (id === DRAW_DRAG_ID) return;

    // Dragging a card that isn't part of the current selection starts a new one.
    if (!useStore.getState().selectedCardIds.includes(id)) {
      useStore.setState({ selectedCardIds: [id] });
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const id = String(event.active.id);
    setDraggingId(null);

    // Taking a card means dragging the deck into your own hand.
    if (id === DRAW_DRAG_ID) {
      if (event.over?.id === HAND_DROP_ID) drawCard();
      return;
    }

    if (event.over?.id !== DISCARD_DROP_ID) return;
    const selection = useStore.getState().selectedCardIds;
    playCards(selection.length > 0 ? selection : [id]);
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDraggingId(null)}
    >
      <div className="table">
        <div className="table__opponents">
          {opponents.map((player) => (
            <PlayerSeat
              key={player.id}
              player={player}
              isCurrent={player.id === game.currentPlayerId}
            />
          ))}
        </div>

        <div className="table__middle">
          <div className="table__centre">
            <DrawPile />
            <div
              className={`table__direction ${game.direction === -1 ? 'is-reversed' : ''}`}
              aria-hidden="true"
            >
              {game.direction === 1 ? '↻' : '↺'}
            </div>
            <DiscardPile />
          </div>
          <LastAction />
        </div>

        <div className="table__you">
          <ActionBar />
          {rejection && (
            <p key={rejection.id} className="rejection" role="status">
              {rejection.text}
            </p>
          )}
          <Hand />
          <div className="table__footer">
            <p className="table__hint">{handHint}</p>
            <label className="assist">
              <input
                type="checkbox"
                checked={highlightPlayable}
                onChange={(event) => setHighlightPlayable(event.target.checked)}
              />
              <span>Highlight playable cards</span>
            </label>
          </div>
        </div>
      </div>

      <DragOverlay dropAnimation={null}>
        {draggingId === DRAW_DRAG_ID ? (
          <div className="drag-overlay drag-overlay--deck">
            {/* A forced pick-up lifts a small fan, so the count is visible in hand. */}
            {Array.from({ length: Math.min(drawCount, 3) }, (_, i) => (
              <CardBack
                key={i}
                style={{
                  marginLeft: i === 0 ? 0 : 'calc(var(--card-w) * -0.78)',
                  transform: `rotate(${(i - 1) * 5}deg)`,
                }}
              />
            ))}
            {drawCount > 1 && <span className="drag-overlay__count">{drawCount}</span>}
          </div>
        ) : draggingCard ? (
          <div className="drag-overlay">
            {sortHand(game.yourHand.filter((c) => selectedCardIds.includes(c.id))).map((card, i) => (
              <PlayingCard
                key={card.id}
                card={card}
                selected
                style={{ marginLeft: i === 0 ? 0 : 'calc(var(--card-w) * -0.6)' }}
              />
            ))}
            {selectedCardIds.length === 0 && <PlayingCard card={draggingCard} selected />}
          </div>
        ) : null}
      </DragOverlay>

      <FlyingCards />
      <SuitPicker />
    </DndContext>
  );
}
